class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("[data-product-form]");
    if (!this.form) return;

    // An add is in flight. See onSubmit for why the disabled button is not enough on its own.
    this._submitting = false;

    this.submitButton = this.form.querySelector('[type="submit"]');
    // Keep any external submit buttons (e.g. the mobile sticky bar) in sync too.
    this.submitButtons = [
      this.submitButton,
      ...document.querySelectorAll(
        `[data-product-form-submit="${this.dataset.productId}"]`,
      ),
    ].filter(Boolean);
    this.errorContainer = this.querySelector("[data-form-error]");
    this.requireSelection = this.hasAttribute("data-require-selection");
    this.selectPrompt = this.dataset.selectPrompt;
    // Opt-in, and off by default — see the note in snippets/product-form.liquid. Presence of
    // the attribute is the whole contract, so the Liquid can drop it entirely when the setting
    // is off and nothing here has to know about theme settings.
    this.requireDelivery = this.hasAttribute("data-require-delivery");
    // What a PASSING check does when it was the buy button that asked for it. Off, the check
    // only hands the button back as "Add to Bag" and the shopper taps again; on, the same tap
    // adds. Theme setting: Delivery > "A passing delivery check also adds to bag".
    this.deliveryCheckAdds = this.hasAttribute("data-delivery-check-adds");
    // True while the button is standing in as the delivery prompt, so a pass can tell "the
    // shopper just answered the prompt" from "the gate was satisfied all along".
    this._awaitingDelivery = false;
    this.deliveryPrompt = this.dataset.deliveryPrompt;
    this.variantSelector =
      customElements
        .get("variant-selector")
        ?.nearest?.(this, this.dataset.productId) ?? null;

    this.form.addEventListener("submit", (event) => this.onSubmit(event));
    this.unlockSubmitButtons();
    this.initVariantSubscriber();
    this.initCartSubscriber();

    // A passing delivery check clears the "Check Delivery" prompt, the same way
    // choosing a size clears "Select Size". Scoped by product id so a Quick View
    // form does not reset the PDP's button underneath it.
    this.handlePincodeChecked = (event) => {
      const { productId, serviceable } = event.detail ?? {};
      if (String(productId) !== String(this.dataset.productId)) return;
      // A refusal is held back only while the gate is on, where the button is showing the
      // "Check Delivery" prompt and refreshing would wipe it. With the gate off there is no
      // prompt to protect, and the button's state does not depend on the verdict either way.
      if (this.requireDelivery && serviceable === false) return;
      // Answered by the block's own button rather than by the buy button, so the next tap is
      // an ordinary add and must not be swallowed as a delivery confirmation.
      if (serviceable) this._awaitingDelivery = false;

      // On a single-variant product there is no <variant-selector>, so both of these are null
      // — and to refreshSubmitButton null means "this combination is not a real variant",
      // which it paints as a disabled "Out of Stock". Nothing about a delivery verdict makes
      // a single-variant product unbuyable, so with nothing to re-derive from, leave the
      // button alone rather than telling the shopper it is sold out. The add below is NOT
      // gated on this: a single-variant product is perfectly addable.
      const variant =
        this.currentVariant ?? this.variantSelector?.getSelectedVariant?.() ?? null;
      if (variant || this.variantSelector) this.refreshSubmitButton(variant);

      this.maybeAddOnDeliveryConfirmed(serviceable);
    };
    document.addEventListener("pincode:checked", this.handlePincodeChecked);
  }

  // A confirmed delivery is itself the add, when the setting says so.
  //
  // Covers the arrow beside the pincode field, which until now only reported a date: the
  // shopper had chosen a size and a pincode and been told we deliver it, then still had to
  // go back up and press the buy button. With the setting on, that confirmation IS the
  // purchase action, from either control.
  //
  // Re-enters the form rather than calling the add directly, so every guard the buy button
  // has applies unchanged — the double-submit lock, sold-out, missing-option, and the
  // delivery gate itself, which now resolves instantly from PINCODE_VERDICTS and costs no
  // second request.
  maybeAddOnDeliveryConfirmed(serviceable) {
    if (!this.deliveryCheckAdds) return;

    // Only a positive verdict. A refusal obviously must not buy anything, and a check that
    // could not be put (network error, no SKU) has reported a failure on screen — turning
    // that into an add would be the one outcome the shopper has no reason to expect. This
    // is deliberately stricter than the buy button's own gate, which treats "could not ask"
    // as "do not block": there, the shopper had already asked to buy.
    if (serviceable !== true) return;

    // The buy button is already mid-add and owns this very check — its own call to
    // ensureDeliverable is what dispatched the event. Adding here as well would put the
    // line in twice.
    if (this._submitting) return;

    // Exactly one form may answer. pincode:checked is a document event, and Quick View
    // renders a SECOND <product-form> for the same product while the PDP's is still in the
    // document (the same hazard VariantSelector.nearest exists for) — both match the product
    // id, so both would add and the line would land twice. Only the form that sits in the
    // product section holding the block that answered is the one being asked: Quick View has
    // no [data-product-section] ancestor and renders no pincode-check of its own, so it
    // falls out here rather than on a guess about which surface is on top.
    const section = this.closest("[data-product-section]");
    if (!section) return;
    if (
      !section.querySelector(
        `pincode-check[data-product-id="${this.dataset.productId}"]`,
      )
    ) {
      return;
    }

    if (typeof this.form?.requestSubmit === "function") this.form.requestSubmit();
    else this.onSubmit(new Event("submit", { cancelable: true }));
  }

  disconnectedCallback() {
    this.unsubscribeVariant?.();
    this.unsubscribeCart?.();
    document.removeEventListener("pincode:checked", this.handlePincodeChecked);
  }

  initCartSubscriber() {
    // Reflect the cart: show "Go to Cart" whenever the selected variant is in
    // the bag. Only variant-selector products are covered (single-variant
    // products keep the default button).
    this.currentVariant = this.variantSelector?.getSelectedVariant?.() ?? null;
    if (!window.CartStore) return;
    window.CartStore.init?.();
    this.unsubscribeCart = window.CartStore.subscribe?.(() => {
      if (this.currentVariant) this.refreshSubmitButton(this.currentVariant);
    });
    if (this.currentVariant) this.refreshSubmitButton(this.currentVariant);
  }

  // The buy buttons ship disabled on size-required products. They live in a real
  // <form action="/cart/add">, and the size buttons hold no form value, so a tap
  // landing before this element upgrades would submit natively and add the hidden
  // id — the first available variant, whatever the shopper had chosen. onSubmit is
  // attached by the time this runs, so the lock can come off. A sold-out button
  // never carries the lock and stays disabled.
  unlockSubmitButtons() {
    for (const button of this.submitButtons) {
      if (!button.hasAttribute("data-hydration-lock")) continue;
      button.removeAttribute("data-hydration-lock");
      button.disabled = false;
    }
  }

  getCurrentVariantId() {
    return parseInt(this.form?.querySelector('input[name="id"]')?.value, 10);
  }

  // "Go to Cart" once a complete selection is already in the bag — but not for a partial
  // (colour-only) selection on a size-required product.
  //
  // Its own method so a click can ask exactly the question the button label answers: the
  // label can only be repainted once CartStore has replied, and onSubmit has to reach the
  // same verdict for a click that lands before that. Only products with a variant-selector
  // flip — a quick-add tile (add-to-cart-button.liquid, the Wizzy results) never shows
  // "Go to Bag" and keeps its existing add-again behaviour.
  shouldGoToCart(variantId) {
    if (!this.variantSelector) return false;

    const selectionComplete =
      !this.requireSelection ||
      this.variantSelector.isSelectionComplete?.() !== false;

    return selectionComplete && this.isVariantInCart(variantId);
  }

  isVariantInCart(variantId) {
    const id = Number(variantId);
    if (!Number.isFinite(id)) return false;
    const items = window.CartStore?.state?.items ?? [];
    return items.some((item) => Number(item.variant_id ?? item.id) === id);
  }

  initVariantSubscriber() {
    if (
      typeof subscribe === "undefined" ||
      typeof PUB_SUB_EVENTS === "undefined"
    )
      return;

    this.unsubscribeVariant = subscribe(
      PUB_SUB_EVENTS.variantChange,
      (data) => {
        if (data.productId !== this.dataset.productId) return;

        if (
          this.requireSelection &&
          !this.variantSelector?.getMissingOptionName()
        ) {
          this.hideError();
        }

        if (data.variant) {
          const variantInput = this.form.querySelector('input[name="id"]');
          if (variantInput) variantInput.value = data.variant.id;
        }

        this.currentVariant = data.variant;
        this.refreshSubmitButton(data.variant);
      },
    );
  }

  setButtonsState(label, disabled, price) {
    this.submitButtons?.forEach((btn) => {
      btn.disabled = disabled;
      const text = btn.querySelector("[data-button-text]");
      if (!text) return;
      // Append the (server-formatted) price only on buttons that opt in via
      // data-atc-with-price — e.g. the main button, not the sticky bar.
      text.textContent =
        price && btn.hasAttribute("data-atc-with-price")
          ? `${label} - ${price}`
          : label;
    });
  }

  refreshSubmitButton(variant) {
    if (!this.submitButtons?.length) return;

    // No size picked yet: `variant` is only the first row matching the colour, so
    // its stock says nothing about the sizes still on offer. Ask the whole partial
    // selection instead — onSubmit prompts for the missing option on click.
    if (this.requireSelection && this.variantSelector?.getMissingOptionName()) {
      const anyAvailable = this.variantSelector.hasAvailableInSelection();
      this.goToCartMode = false;
      this.setButtonsState(
        anyAvailable
          ? window.theme?.strings?.addToBag ?? "Add To Bag"
          : window.theme?.strings?.soldOut ?? "Out of Stock",
        !anyAvailable,
        anyAvailable ? variant?.price_formatted : null,
      );
      return;
    }

    // A combination that isn't a real variant reads the same as one that is out
    // of stock: the storefront has no separate "unavailable" state to show.
    if (!variant || !variant.available) {
      this.goToCartMode = false;
      this.setButtonsState(
        window.theme?.strings?.soldOut ?? "Out of Stock",
        true,
      );
      return;
    }

    if (this.shouldGoToCart(variant.id)) {
      this.setButtonsState(window.theme?.strings?.goToCart ?? "Go to Bag", false);
      this.goToCartMode = true;
      return;
    }

    // A complete, buyable selection. Products with no required option land here
    // straight away; size-required ones only after a size is picked, since the
    // partial-selection branch above owns everything before that.
    this.goToCartMode = false;
    this.setButtonsState(
      window.theme?.strings?.addToBag ?? "Add To Bag",
      false,
      variant.price_formatted,
    );
  }

  // Whether this add may proceed. The PDP's own delivery block is the gate: it
  // owns the pincode field, the verdict and the messages, so there is no modal —
  // an unmet delivery check prompts exactly the way an unselected size does.
  //
  // Absent block, absent SKU, or a check that could not be put all return true.
  // The gate must never be the reason a shopper cannot buy something we simply
  // failed to ask about.
  async ensureDeliverable() {
    // Off by default: an unserviceable pincode no longer stops the add, and the button keeps
    // saying "Add to Bag" rather than flipping to "Check Delivery". The shopper is not being
    // let through to a delivery we cannot make — <cart-serviceability> stamps the line in the
    // bag and both cart surfaces close GoKwik checkout on it. The gate just moved to where the
    // shopper can act on it, instead of standing between them and a bag they can still fill.
    if (!this.requireDelivery) return true;

    const variant =
      this.currentVariant ?? this.variantSelector?.getSelectedVariant?.() ?? null;
    const sku = variant?.sku || this.dataset.sku || "";
    if (!sku) return true;

    // Scoped to this product. Quick View renders a second <product-form> while the
    // PDP's own block is still in the document, and a document-wide lookup would
    // drive the wrong one.
    const deliveryCheck = document.querySelector(
      `pincode-check[data-product-id="${this.dataset.productId}"]`,
    );
    if (!deliveryCheck?.ensureDeliverable) return true;

    // Read before the await: a passing check dispatches pincode:checked, whose handler clears
    // this. The question here is what the button was showing when it was TAPPED.
    const wasPrompting = this._awaitingDelivery;

    const deliverable = await deliveryCheck.ensureDeliverable(sku);
    if (!deliverable) {
      // Same shape as the unselected-option prompt: the button states what is
      // missing, and flips back once the check passes.
      this._awaitingDelivery = true;
      this.setButtonsState(this.deliveryPrompt ?? "Check Delivery", false);
      return false;
    }

    this._awaitingDelivery = false;

    // The tap that answered the prompt. Unless the setting says otherwise it buys nothing on
    // its own: it confirms delivery, hands the button back as "Add to Bag", and leaves the
    // decision to add with the shopper. refreshSubmitButton is called explicitly because a
    // cached verdict resolves without dispatching pincode:checked, so the handler above
    // cannot be relied on to have repainted it.
    if (wasPrompting && !this.deliveryCheckAdds) {
      this.refreshSubmitButton(variant);
      return false;
    }

    return true;
  }

  async onSubmit(event) {
    event.preventDefault();

    // A second click must never start a second add. Two /cart/add.js requests in flight
    // together validate against the same pre-add cart and Shopify lets both through, which is
    // how a variant with one unit in stock reached the bag twice. setLoading() disables the
    // buttons, but only after the awaits below — this lock is what actually closes the window.
    if (this._submitting || this.submitButton?.disabled) return;

    if (this.requireSelection) {
      const missingOption = this.variantSelector?.getMissingOptionName();
      if (missingOption) {
        // Now prompt on the button (it read "Add to cart" until this click),
        // and nudge the selector. Selecting the option flips it back via
        // refreshSubmitButton.
        this.setButtonsState(
          this.selectPrompt ?? `Select ${missingOption}`,
          false,
        );
        // shakeMissingOptions scrolls the missing group into view itself when it
        // is off screen, so both this and the delivery check prompt the same way.
        this.variantSelector?.shakeMissingOptions?.();
        return;
      }
    }

    this._submitting = true;
    this.setLoading(true);
    this.hideError();

    // Delivery gate. Runs inside the submit lock so a second tap cannot start a
    // parallel add while the modal is open, and releases it on refusal — the
    // shopper is left exactly where they were, with no line added and no error
    // banner, because backing out of the modal is not a failure.
    if (!(await this.ensureDeliverable())) {
      this._submitting = false;
      this.setLoading(false);
      return;
    }

    const cartDrawer = document.querySelector("cart-drawer");
    const sectionId = cartDrawer?.dataset?.sectionId;
    const sections = sectionId ? [sectionId] : [];
    const formData = new FormData(this.form);
    const variantId = parseInt(formData.get("id"), 10);

    // Asked before the decision below rather than after it. Until the store has answered, the
    // button is still showing the server-rendered "Add To Bag" and goToCartMode is false — so
    // a click landing in that window (refresh the page, then tap straight away) re-adds a line
    // that is already in the bag. Waiting here makes that click behave like one landing a
    // moment later. init() memoises its promise, so on a settled page this costs nothing.
    await CartStore.init();

    // Selected variant is already in the bag — open the cart instead of re-adding.
    if (this.goToCartMode || this.shouldGoToCart(variantId)) {
      this.setLoading(false);
      // Repaint the label the click arrived too early to see. currentVariant is still unset
      // if no variantChange has fired yet, so fall back to asking the selector directly.
      const selected =
        this.currentVariant ?? this.variantSelector?.getSelectedVariant?.();
      if (selected) this.refreshSubmitButton(selected);
      cartDrawer?.open();
      this._submitting = false;
      return;
    }

    const wasEmpty = (CartStore.state?.item_count ?? 0) === 0;

    // Booked before the drawer opens, not after: open() runs the source_channel heal, which
    // skips itself while a write is outstanding. Booked afterwards, that heal raced this add
    // and could publish the pre-add cart over the top of it.
    CartStore.beginOp();
    cartDrawer?.open();

    // Open on the click rather than on the response, and stand a placeholder in for the line
    // that is on its way. This is a loading state, not a guess at the cart: no quantity, price
    // or total is written here — those only ever arrive rendered by the server.
    const emptyEl = cartDrawer?.querySelector("[data-cart-empty]");
    const itemsEl = cartDrawer?.querySelector("[data-cart-items]");
    const skeletonEl = cartDrawer?.querySelector("[data-cart-skeleton]");
    if (emptyEl) emptyEl.hidden = true;

    let shimmerEl = null;

    if (wasEmpty) {
      if (itemsEl) itemsEl.hidden = false;
      if (skeletonEl) skeletonEl.hidden = false;
    } else {
      const existingItem = CartStore.state?.items?.find(
        (item) => item.variant_id === variantId,
      );
      if (existingItem) {
        const row = cartDrawer?.querySelector(
          `[data-item-key="${existingItem.key}"]`,
        );
        const parent = row?.parentElement;
        shimmerEl = (parent?.children.length === 1 ? parent : row) ?? null;
        shimmerEl?.classList.add("animate-pulse");
      } else {
        if (skeletonEl) skeletonEl.hidden = false;
      }
    }

    try {
      const data = await CartAPI.add(formData, sections);

      this.pushAddToCartEvent(data);

      // Delegate to the drawer rather than swapping markup here: it owns which regions may be
      // replaced, and GoKwik's injected checkout node has to stay out of that set.
      if (sectionId && data.sections?.[sectionId] && cartDrawer) {
        cartDrawer.renderSection(data.sections[sectionId]);
      }

      // The drawer is already showing the real line by now — this second call is for the store,
      // which needs a whole cart. add.js answers with the line it added, not the cart, and
      // coupon eligibility and the "Go to Bag" label both read from CartStore.state.
      let cartData = await CartAPI.get();
      cartData = await CartAPI.ensureSourceChannel(cartData);
      CartStore.settle(cartData);

      // Keep the cart drawer open on the freshly rendered contents. Only fall back to the cart
      // page if there's no drawer on this page.
      if (!cartDrawer) {
        window.location.href = `${CartAPI.getRoot()}cart`;
      }
      return;
    } catch (error) {
      CartStore.failOp();
      const message =
        error?.description ??
        error?.message ??
        window.theme?.strings?.cartError ??
        "Something went wrong.";
      if (cartDrawer) {
        cartDrawer.showError(message);
        // Put the regions back the way the cart actually is. Nothing else will: the drawer only
        // reinstates them when it re-renders a section, and a failed add has no markup to render
        // — so an empty cart would otherwise be left showing an empty item list and no empty
        // state at all.
        await this.revertDrawerLoadingState(cartDrawer);
      } else {
        this.showError(message);
      }
    } finally {
      this._submitting = false;
      this.setLoading(false);
      const finalSkeletonEl = cartDrawer?.querySelector("[data-cart-skeleton]");
      if (finalSkeletonEl) finalSkeletonEl.hidden = true;
      shimmerEl?.classList.remove("animate-pulse");
    }
  }

  // Restores the empty/filled regions after a failed add, from the server rather than from
  // CartStore.
  //
  // It used to derive them from CartStore.state.item_count, which is wrong in exactly the
  // case that matters: a failed add leaves state holding whatever it held before, and if
  // another add is still in flight (or init has not answered at all) that is the cart from
  // *before* anything was added. Reading it as empty is what painted the empty state over a
  // bag that had items in it, under a header count that was still correct. The local guess
  // survives only as a fallback for when the fetch comes back with nothing.
  // GA4 `add_to_cart` for GTM (container defined in layout/theme.liquid).
  //
  // Called only after /cart/add.js resolves, so a rejected add — the sold-out race the
  // submit lock above guards — can never report a conversion that did not happen.
  //
  // Most of the payload comes straight off the add response rather than being
  // re-rendered into the DOM: it already carries both ids, name, variant title, url,
  // image, vendor, price and quantity, and a second copy in Liquid would be a second
  // source of truth for the same numbers. Only the two values it does NOT return are
  // read from data attributes — the style number metafield and the category path.
  //
  // mrp is compare_at_price, which is per-variant, so it comes from the selected
  // variant rather than the product. currentVariant is null until a variantChange has
  // fired, hence the same selector fallback onSubmit uses.
  //
  // Dividing by 100 is unit conversion, not display formatting: GA4 wants a number and
  // Shopify counts in minor units. Nothing here is ever shown to a shopper.
  pushAddToCartEvent(response) {
    const line = response?.items?.[0] ?? response;
    if (!line?.variant_id) return;

    const variant =
      this.currentVariant ?? this.variantSelector?.getSelectedVariant?.() ?? null;
    const compareAt = Number(variant?.compare_at_price) || 0;

    const item = {
      item_id: String(line.variant_id),
      item_name: line.product_title,
      item_brand: line.vendor,
      item_variant: line.variant_title,
      item_link: line.url,
      item_image_url: line.image,
      price: line.price / 100,
      quantity: line.quantity,
    };

    const styleNumber = this.dataset.analyticsStyleNumber;
    if (styleNumber) item.style_id = styleNumber;

    const category = this.dataset.analyticsCategory;
    if (category) item.item_category = category;

    // Only when there is a genuine was-price: compare_at_price is 0 on anything not
    // discounted, and reporting mrp 0 would read as a 100% markdown.
    if (compareAt > line.price) item.mrp = compareAt / 100;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "add_to_cart",
      ecommerce: {
        currency: window.Shopify?.currency?.active,
        items: [item],
      },
    });
  }

  async revertDrawerLoadingState(cartDrawer) {
    if (await cartDrawer.renderFromServer()) return;

    const isEmpty = (CartStore.state?.item_count ?? 0) === 0;
    const emptyEl = cartDrawer.querySelector("[data-cart-empty]");
    const itemsEl = cartDrawer.querySelector("[data-cart-items]");
    if (emptyEl) emptyEl.hidden = !isEmpty;
    if (itemsEl) itemsEl.hidden = isEmpty;
  }

  setLoading(isLoading) {
    this.submitButtons?.forEach((btn) => {
      btn.disabled = isLoading;
      const buttonText = btn.querySelector("[data-button-text]");
      const buttonLoader = btn.querySelector("[data-button-loader]");
      if (buttonText) buttonText.classList.toggle("invisible", isLoading);
      if (buttonLoader) {
        buttonLoader.classList.toggle("hidden", !isLoading);
        buttonLoader.classList.toggle("flex", isLoading);
      }
    });
  }

  showError(message) {
    if (!this.errorContainer) return;
    this.errorContainer.textContent = message;
    this.errorContainer.hidden = false;
  }

  hideError() {
    if (!this.errorContainer) return;
    this.errorContainer.textContent = "";
    this.errorContainer.hidden = true;
  }
}

if (!customElements.get("product-form")) {
  customElements.define("product-form", ProductForm);
}
